import { useCallback, useMemo } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { CalendarEvent } from "../types/event";
import {
	EventSearchPage,
	SEARCH_PAGE_SIZE,
	fetchEventSearch,
} from "./useEventManager";

/**
 * Events matching a search term, across the whole collection.
 *
 * ## Why this is not useBrowseEvents with a `search` argument
 *
 * They page over different things. `useBrowseEvents` walks TIME, a month per
 * request, and drops the far month once four are held, because a month of this
 * calendar is around 8,000 events and holding a year of them would serve
 * nobody. A search walks MATCHES: the whole result set is the answer, it is
 * usually small, and evicting the front of it as you scroll would mean the
 * search quietly stopped containing what it found.
 *
 * That difference is also the bug this hook exists to fix. While the search ran
 * in memory over whatever `useBrowseEvents` had fetched, its corpus was four
 * months wide, so events matching the term in any other month were not merely
 * unlisted — they were uncounted, and the rail's facet counts read as totals.
 *
 * ## No date bounds, and no eviction
 *
 * `maxPages` is deliberately absent. Pages are 500 rows and are only ever
 * fetched on an explicit Load more, so what accumulates is what someone asked
 * for by pressing a button, not what a scroll wheel dragged past.
 */

export interface EventSearch {
	/** Every page fetched so far, in start order. */
	events: CalendarEvent[];
	/**
	 * Matches on the server, before the rail's client-side facets and before
	 * `limit`. Null when the API did not say, which an older deployment will not.
	 */
	total: number | null;
	/** First page in flight — the view shows a spinner rather than an empty state. */
	loading: boolean;
	/** A later page in flight, with results already on screen. */
	loadingMore: boolean;
	/** True while the server holds matches this hook has not fetched. */
	hasMore: boolean;
	loadMore: () => void;
}

const EMPTY: CalendarEvent[] = [];

export function useEventSearch(
	search: string,
	categories: string[],
	enabled: boolean,
	tags: string[] = [],
): EventSearch {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	// Sorted, so ticking A then B and ticking B then A share one cache entry —
	// the rule useBrowseEvents and useEventManager both follow.
	const categoryKey = useMemo(
		() => [...categories].sort().join(","),
		[categories],
	);

	/* Lower-cased as well as sorted: the API matches tags case-insensitively, so
	   "Sport" and "sport" are one request and must not be two cache entries. */
	const tagKey = useMemo(
		() =>
			[...tags]
				.map((t) => t.toLocaleLowerCase("en-AU"))
				.sort()
				.join(","),
		[tags],
	);

	const active = enabled && isAuthenticated && search.length > 0;

	const query = useInfiniteQuery<EventSearchPage>({
		queryKey: ["events", "search", "v1", search, categoryKey, tagKey],
		queryFn: ({ pageParam }) =>
			fetchEventSearch(instance, search, categories, pageParam as number, tags),
		initialPageParam: 1,
		getNextPageParam: (last, all) => {
			const loaded = all.reduce((n, page) => n + page.events.length, 0);
			/* No total means an API that predates `with_total`. Fall back to the
			   only other signal there is: a page that came back full probably has
			   more behind it, and a short one is the end. That over-offers Load
			   more exactly once, on a result set that is an exact multiple of the
			   page size, which is a better failure than hiding results. */
			if (last.total === null) {
				return last.events.length === SEARCH_PAGE_SIZE
					? last.page + 1
					: undefined;
			}
			return loaded < last.total ? last.page + 1 : undefined;
		},
		enabled: active,
	});

	const events = useMemo(
		() =>
			query.data
				? (query.data.pages.flatMap((page) => page.events) as CalendarEvent[])
				: EMPTY,
		[query.data],
	);

	/* From the LAST page, not the first: every page carries the same count, but
	   a re-count on a later page is the fresher reading of a collection that
	   other people are editing while this list is open. */
	const pages = query.data?.pages;
	const total = pages?.length ? (pages[pages.length - 1].total ?? null) : null;

	const { fetchNextPage } = query;
	const loadMore = useCallback(() => {
		fetchNextPage();
	}, [fetchNextPage]);

	return {
		events,
		total,
		/* `isFetching` rather than `isLoading`, so re-running an edited term shows
		   the spinner instead of leaving the previous term's results on screen
		   looking like an answer to the new one. */
		loading: active && query.isFetching && !query.isFetchingNextPage,
		loadingMore: query.isFetchingNextPage,
		hasMore: Boolean(query.hasNextPage),
		loadMore,
	};
}
